import { useNavigate } from 'react-router-dom';
import { ROUTES } from 'src/utils/constants/routes';

// ─── Param types ─────────────────────────────────────────────────────────────

/**
 * All query params recognised by the onboarding app.
 * Keys mirror the SEARCH_PARAMS constant so callers never need to know the
 * raw query-string key names.
 */
export type OnboardingRouteParams = {
  journeyType?: string | null;
  dataType?: string | null;
  digio_doc_id?: string | null;
  success?: string | null;
  mode?: string | null;
  status?: string | null;
};


// Explicit allowlist of every path this app is permitted to navigate to.
// Derived solely from the hard-coded ROUTES constant — never from user input
// or runtime data — so the set cannot be widened at runtime.
const ALLOWED_PATHS = new Set(Object.values(ROUTES));

// ─── Route safety ─────────────────────────────────────────────────────────────

/**
 * Extracts and validates the base path from a route string.
 *
 * Security rules (applied in order):
 *  1. Must be a non-empty string.
 *  2. Must NOT be an absolute URL (blocks `https://…` and `//…` open-redirect
 *     vectors). The app serves on multiple origins (groww.in, 915.groww.in,
 *     next/uat envs) so we never derive safety from the hostname — the path
 *     allowlist is the only gate.
 *  3. Must NOT contain path-traversal segments (`..`) that could escape the
 *     allowlisted paths.
 *  4. Must start with `/` — enforces a relative-from-root path, ruling out
 *     relative traversals such as `../secret`.
 *  5. Base path (before `?`) must be an exact match in ALLOWED_PATHS.
 *
 * Returns the sanitised base path on success, or `null` if any rule fails.
 */
const getSafeBasePath = (route: string): string | null => {
  if (!route || typeof route !== 'string') return null;

  // Rule 2 — block absolute and protocol-relative URLs
  if (/^(https?:)?\/\//i.test(route)) return null;

  // Rule 4 — must be an absolute path within the app
  if (!route.startsWith('/')) return null;

  const basePath = route.split('?')[0];

  // Rule 3 — block path traversal
  if (basePath.includes('..')) return null;

  // Rule 5 — must be an explicitly allowed path
  if (!ALLOWED_PATHS.has(basePath)) return null;

  return basePath;
};

// ─── Param validation ─────────────────────────────────────────────────────────

/**
 * Returns true if the value is a non-empty string.
 * Null / undefined are allowed intentionally (they clear the param).
 */
const isValidParamValue = (value: string | null | undefined): boolean => {
  if (value === null || value === undefined) return true;
  return typeof value === 'string' && value.trim().length > 0;
};

/**
 * Validates all supplied params.
 * Returns an array of error messages; empty array means valid.
 */
const validateParams = (params: OnboardingRouteParams): string[] => {
  const errors: string[] = [];
  (Object.entries(params) as [keyof OnboardingRouteParams, string | null | undefined][]).forEach(
    ([key, value]) => {
      if (!isValidParamValue(value)) {
        errors.push(`Invalid value for param "${key}": "${value}"`);
      }
    }
  );
  return errors;
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

type NavigateOptions = {
  /** Use history replace instead of push (default: true) */
  replace?: boolean;
};

/**
 * useNavigateToRoute
 *
 * Secure, smart navigation helper for the onboarding app.
 *
 * Rules:
 *  • The target route is validated against a hard-coded allowlist before any
 *    navigation occurs. Invalid routes are rejected outright — no fallback.
 *  • Params are validated before navigation — invalid values are skipped.
 *  • If the destination base path is identical to the current path (i.e. a
 *  • Passing null / undefined for a param explicitly removes it from the URL.
 *  • Any route with a different base path (e.g. /login, /onboarding/futures-*)
 *    starts with a clean query string — no param leakage across route boundaries.
 *  • Absolute URLs and path-traversal patterns are blocked at the route-validation
 *    layer, preventing open-redirect attacks regardless of the serving domain.
 */
export const useNavigateToRoute = () => {
  const navigate = useNavigate();

  const navigateToRoute = (
    route: string,
    newParams: OnboardingRouteParams = {},
    options: NavigateOptions = { replace: true }
  ) => {
    // ── Step 1: validate & sanitise the target route ──────────────────────────
    const targetBase = getSafeBasePath(route);

    if (!targetBase) {
      // Hard reject — do not navigate to unknown or unsafe destinations
      console.error('[useNavigateToRoute] Blocked navigation to disallowed route:', route);
      return;
    }

    // ── Step 2: validate supplied params ─────────────────────────────────────
    const errors = validateParams(newParams);
    if (errors.length > 0) {
      errors.forEach((err) => console.warn('[useNavigateToRoute]', err));
    }

    const mergedParams = new URLSearchParams();

    // ── Step 4: apply caller-supplied params (override or clear) ──────────────
    (
      Object.entries(newParams) as [keyof OnboardingRouteParams, string | null | undefined][]
    ).forEach(([key, value]) => {
      if (!isValidParamValue(value)) return; // already warned above, skip


      if (value === null || value === undefined) {
        mergedParams.delete(key);
      } else {
        mergedParams.set(key, value);
      }
    });

    const queryString = mergedParams.toString();
    const finalRoute = queryString ? `${targetBase}?${queryString}` : targetBase;

    navigate(finalRoute, { replace: options.replace ?? true });
  };

  return { navigateToRoute };
};
